import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";
import bannerBg from "../../img/trade/banner-bg.png";
import bannerGrid from "../../img/trade/banner-grid.svg";
import bannerHero from "../../img/trade/banner-hero.png";

const REVEAL_COLS = 5;
const REVEAL_ROWS = 4;

const REVEAL_TILES = Array.from({ length: REVEAL_COLS * REVEAL_ROWS }, (_, index) => {
  const col = index % REVEAL_COLS;
  const row = Math.floor(index / REVEAL_COLS);
  const order = (index * 37) % (REVEAL_COLS * REVEAL_ROWS);
  const horizontal = (index + row) % 2 === 0;

  return {
    id: index,
    className: horizontal ? "banner-reveal-x" : "banner-reveal-y",
    style: {
      left: `${(col / REVEAL_COLS) * 100}%`,
      top: `${(row / REVEAL_ROWS) * 100}%`,
      width: `${100 / REVEAL_COLS}%`,
      height: `${100 / REVEAL_ROWS}%`,
      animationDelay: `${80 + order * 60}ms`,
      transformOrigin: horizontal
        ? ((index + row) % 2 === 0 ? "left" : "right")
        : ((index + col) % 2 === 0 ? "top" : "bottom"),
    },
  };
});

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);

  return (
    <section className="relative bg-[#020202] overflow-hidden pt-[56px] xl:pt-0 min-h-[900px] xl:h-[955px]">

      {/* Desktop 1512px visual container */}
      <div className="hidden xl:block absolute left-1/2 -translate-x-1/2 top-0 w-[1512px] h-[955px] overflow-clip pointer-events-none">
        <div className="absolute left-0 top-[6px] w-[1512px] h-[949px] overflow-hidden">
          <img src={bannerBg} alt="" aria-hidden className="absolute left-0 max-w-none w-full" style={{ height: "119.49%", top: "-0.05%" }} />
        </div>
        <img src={bannerGrid} alt="" aria-hidden className="absolute inset-0 w-full h-full" />
        {/* Hero trading platform image */}
        <div className="absolute overflow-hidden" style={{ left: "420px", top: "347px", width: "880px", height: "885px" }}>
          <img src={bannerHero} alt="" aria-hidden className="absolute top-0 left-0 max-w-none" style={{ width: "204.47%", height: "100%" }} />
        </div>
      </div>

      {/* Mobile background */}
      <div className="xl:hidden absolute inset-0 overflow-hidden pointer-events-none">
        <img src={bannerBg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <img src={bannerGrid} alt="" aria-hidden className="absolute inset-0 w-full h-full" />
      </div>

      {/* Mobile hero image */}
      <div className="xl:hidden absolute pointer-events-none" style={{ left: "-109px", top: "575px", width: "452px", height: "455px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img src={bannerHero} alt="" aria-hidden className="absolute top-0 left-0 max-w-none h-full" style={{ width: "204.47%" }} />
        </div>
      </div>

      {/* Left gradient fade desktop */}
      <div className="hidden xl:block absolute inset-0 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to right, #020202 0%, #020202 30%, rgba(2,2,2,0.7) 50%, rgba(2,2,2,0) 70%)" }} />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-[120px] pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to top, #020202 0%, rgba(2,2,2,0) 100%)" }} />

      {/* Animated tiles (desktop only) */}
      <div className="hidden xl:block absolute top-[106px] left-1/2 -translate-x-1/2 z-[3] w-full max-w-[1220px] h-[744px] pointer-events-none overflow-hidden">
        {REVEAL_TILES.map((tile) => (
          <span key={tile.id} className={`banner-reveal-tile ${tile.className}`} style={tile.style} />
        ))}
      </div>

      {/* ── Desktop stat cards (positioned in 1512px space) ── */}
      {/* Black card: 25+ Liquidity Providers */}
      <div className="hidden xl:flex absolute z-[3] bg-black rounded-[20px] flex-col justify-center gap-[6px] px-[38px]"
        style={{ left: "calc(50% + 878px - 756px)", top: "94px", width: "243px", height: "186px" }}>
        <span className="font-['Montserrat'] font-extrabold text-[80px] text-[#ee5414] leading-[1.2] tracking-[-0.4px]">{t("trade.stat1.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">{t("trade.stat1.label")}</span>
      </div>

      {/* White blur card: 1:100 Leverage */}
      <div className="hidden xl:flex absolute z-[3] backdrop-blur-[5px] bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[16px] flex-col justify-center gap-[6px] px-[38px]"
        style={{ left: "calc(50% + 878px - 756px)", top: "280px", width: "244px", height: "186px" }}>
        <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">{t("trade.stat2.prefix")}</span>
        <span className="font-['Montserrat'] font-extrabold text-[64px] text-white leading-[1.2] tracking-[-0.4px]">{t("trade.stat2.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">{t("trade.stat2.label")}</span>
      </div>

      {/* Black blur card: 40ms Execution */}
      <div className="hidden xl:flex absolute z-[3] backdrop-blur-[5px] bg-[rgba(0,0,0,0.4)] border border-[rgba(255,255,255,0.1)] rounded-[16px] flex-col justify-center gap-[6px] px-[38px]"
        style={{ left: "calc(50% + 877px - 756px)", top: "652px", width: "244px", height: "186px" }}>
        <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">{t("trade.stat3.prefix")}</span>
        <span className="font-['Montserrat'] font-extrabold text-[64px] text-white leading-[1.2] tracking-[-0.4px]">{t("trade.stat3.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">{t("trade.stat3.label")}</span>
      </div>

      {/* Orange card: 61+4 FX Pairs */}
      <div className="hidden xl:flex absolute z-[3] bg-[#ee5414] rounded-[20px] flex-col justify-center gap-[6px] px-[38px]"
        style={{ left: "calc(50% + 1122px - 756px)", top: "652px", width: "244px", height: "186px" }}>
        <span className="font-['Montserrat'] font-extrabold text-[64px] text-white leading-[1.2] tracking-[-0.4px]">{t("trade.stat4.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">{t("trade.stat4.label")}</span>
      </div>

      {/* ── Mobile stat cards ── */}
      {/* Black card: 25+ */}
      <div className="xl:hidden absolute z-[3] bg-black rounded-[20px] flex flex-col justify-center gap-[4px] px-[20px]"
        style={{ left: "20px", top: "462px", width: "165px", height: "80px" }}>
        <span className="font-['Montserrat'] font-extrabold text-[24px] text-[#ee5414] leading-[1.2] tracking-[-0.4px]">{t("trade.stat1.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">{t("trade.stat1.label")}</span>
      </div>

      {/* White blur card: 1:100 */}
      <div className="xl:hidden absolute z-[3] backdrop-blur-[5px] bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[16px] flex flex-col justify-center gap-[4px] px-[20px]"
        style={{ left: "205px", top: "462px", width: "165px", height: "80px" }}>
        <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">{t("trade.stat2.prefix")}</span>
        <span className="font-['Montserrat'] font-extrabold text-[24px] text-white leading-[1.2] tracking-[-0.4px]">{t("trade.stat2.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">{t("trade.stat2.label")}</span>
      </div>

      {/* Orange card: 61+4 mobile */}
      <div className="xl:hidden absolute z-[3] bg-[#ee5414] rounded-[20px] flex flex-col justify-center gap-[4px] px-[20px]"
        style={{ left: "20px", top: "562px", width: "165px", height: "80px" }}>
        <span className="font-['Montserrat'] font-extrabold text-[24px] text-white leading-[1.2] tracking-[-0.4px]">{t("trade.stat4.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">{t("trade.stat4.label")}</span>
      </div>

      {/* Black blur card: 40ms mobile */}
      <div className="xl:hidden absolute z-[3] backdrop-blur-[5px] bg-[rgba(0,0,0,0.4)] border border-[rgba(255,255,255,0.1)] rounded-[16px] flex flex-col justify-center gap-[4px] px-[20px]"
        style={{ left: "20px", top: "787px", width: "165px", height: "80px" }}>
        <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">{t("trade.stat3.prefix")}</span>
        <span className="font-['Montserrat'] font-extrabold text-[24px] text-white leading-[1.2] tracking-[-0.4px]">{t("trade.stat3.num")}</span>
        <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">{t("trade.stat3.label")}</span>
      </div>

      {/* ── Text content ── */}
      <div className="relative z-[4] glcontainer">

        {/* Desktop */}
        <div className="hidden xl:flex flex-col gap-[211px] pt-[145px] w-[703px]">
          <div className="flex flex-col gap-[20px]">
            <p className="font-['Inter'] font-medium text-[14px] text-[#636363] tracking-[1.4px] uppercase leading-[1.2]">
              {t("trade.banner.subtitle")}
            </p>
            <h1 className="font-['Montserrat'] font-black text-[64px] text-[#e1e1e1] tracking-[-1.28px] leading-[1.2] max-w-[572px]">
              {t("trade.banner.title")}
            </h1>
          </div>
          <div className="flex flex-col gap-[46px] w-[488px]">
            <p className="font-['Inter'] font-normal text-[16px] text-[#bebebe] leading-[1.4]">
              {t("trade.banner.desc")}
            </p>
            <AnimatedCTA href={link}>{t("trade.banner.cta")}</AnimatedCTA>
          </div>
        </div>

        {/* Mobile */}
        <div className="xl:hidden flex flex-col gap-[20px] pb-[60px]">
          <p className="font-['Inter'] font-medium text-[12px] text-[#636363] tracking-[1.4px] uppercase leading-[1.2]">
            {t("trade.banner.subtitle")}
          </p>
          <h1 className="font-['Montserrat'] font-black text-[32px] text-[#e1e1e1] tracking-[-1.28px] leading-[1.2]">
            {t("trade.banner.title")}
          </h1>
          <p className="font-['Inter'] font-normal text-[14px] text-[#bebebe] leading-[1.4]">
            {t("trade.banner.desc")}
          </p>
          <AnimatedCTA href={link} mobile full>{t("trade.banner.cta")}</AnimatedCTA>
        </div>

      </div>
    </section>
  );
}

export default Banner;
